import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Lightbulb, Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";

interface TourStep {
  id: string;
  selector: string;
  title: string;
  message: string;
  position?: "top" | "bottom" | "left" | "right" | "auto";
}

interface SimpleTourProps {
  isActive: boolean;
  dismissedTours: Set<string>;
  onClose?: () => void;
  onDismiss?: () => void;
}

// Define tour steps - no page restriction, just match by data-tour attribute
const TOUR_STEPS: TourStep[] = [
  // Dashboard page
  {
    id: "dashboard-welcome",
    selector: "[data-tour=\"dashboard-welcome\"]",
    title: "Welcome to Shreds!",
    message: "This is your main dashboard where you can see all your projects and get started.",
    position: "bottom" // Force bottom positioning
  },
  
  // Project list
  {
    id: "projects-list",
    selector: "[data-tour=\"projects-list\"]",
    title: "Create a New Project",
    message: "Start by creating your first project here",
    position: "top" // Force top positioning
  },
  
  // Create project page
  {
    id: "projects-name-stack",
    selector: "[data-tour=\"projects-name-stack\"]",
    title: "Get Started",
    message: "Enter a name and main package (usually reverse dns) for your project",
    position: "left" // Force left positioning
  },
  
  // Specs page - choose AI or upload
  {
    id: "projects-chose-specs",
    selector: "[data-tour=\"projects-chose-specs\"]",
    title: "Generate Specs",
    message: "Use our powerful AI to generate specs for your project from simple description.",
    position: "left" // Use smart positioning (default behavior)
  },
  
  //copy ai prompt
  {
    id: "copy-enhanced-prompt",
    selector: "[data-tour=\"copy-enhanced-prompt\"]",
    title: "Copy AI Prompt",
    message: "Copy and modify this enhanced prompt to use with AI for generating specs.",
    position: "bottom" // Use smart positioning (default behavior)
  },
  
  //select service for code generation
  {
    id: "validate-architcture",
    selector: "[data-tour=\"validate-architcture\"]",
    title: "Select a Service to Start Development",
    message: "Click on any service to start the development cycle. Services with existing code appear first with a RESUME badge, while new services show a START badge.",
    position: "right" // Use smart positioning (default behavior)
  },
  
  //validate model
  {
    id: "validate-model",
    selector: "[data-tour=\"validate-model\"]",
    title: "Review, Modify and Validate",
    message: "Review Specs bellow and validate them before continuing.",
    position: "left" // Use smart positioning (default behavior)
  },
  
  //generate input/outputs data-tour="generate-entries"
  {
    id: "generate-entries",
    selector: "[data-tour=\"generate-entries\"]",
    title: "Generate Inputs/Outputs",
    message: "Generate inputs and outputs for your project using AI or manually. This allows you to force data structure in the generated code.",
    position: "left" // Use smart positioning (default behavior)
  },
  //generate code data-tour="generate-shred"
  {
    id: "generate-shred",
    selector: "[data-tour=\"generate-shred\"]",
    title: "Last Step: Generate Code",
    message: "Review carefully the plan above, update it if needed, then click \"Generate Shred\" to create your project code.",
    position: "left" // Use smart positioning (default behavior)
  }


];

const SimpleTour: React.FC<SimpleTourProps> = ({
                                                 isActive,
                                                 dismissedTours,
                                                 onClose,
                                                 onDismiss
                                               }) => {
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState<TourStep | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [highlightedElement, setHighlightedElement] = useState<string | null>(null);
  
  const overlayRef = useRef<HTMLDivElement>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const targetElementRef = useRef<Element | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  
  // Find current step by scanning all tour elements on the page
  const findCurrentStep = useCallback(() => {
    const currentPath = location.pathname;
    console.log("SimpleTour: Scanning for tour elements on path:", currentPath, "isActive:", isActive);
    
    if (!isActive) {
      console.log("SimpleTour: Tour not active, returning null");
      return null;
    }
    
    // Check if current page has been dismissed
    if (dismissedTours.has(currentPath)) {
      console.log("SimpleTour: Tour dismissed for current page, returning null");
      return null;
    }
    
    // Scan for all elements with data-tour attributes on the current page
    const tourElements = document.querySelectorAll("[data-tour]");
    console.log("SimpleTour: Found tour elements on page:", tourElements.length);
    
    // Check each tour element against our TOUR_STEPS
    for (const element of tourElements) {
      const tourId = element.getAttribute("data-tour");
      if (!tourId) continue;
      
      // Find matching step in TOUR_STEPS
      const matchingStep = TOUR_STEPS.find(step => step.id === tourId);
      if (matchingStep) {
        // Check if element is visible
        const rect = element.getBoundingClientRect();
        const isVisible = rect.width > 0 && rect.height > 0;
        
        if (isVisible) {
          console.log("SimpleTour: Found matching visible tour element:", tourId, "position:", matchingStep.position || "auto");
          return matchingStep;
        }
      }
    }
    
    console.log("SimpleTour: No matching tour steps found");
    return null;
  }, [location.pathname, isActive, dismissedTours]);
  
  // Setup MutationObserver to watch for dynamically added elements
  const setupMutationObserver = useCallback(() => {
    if (!isActive || dismissedTours.has(location.pathname)) {
      return;
    }
    
    // Disconnect existing observer
    if (mutationObserverRef.current) {
      mutationObserverRef.current.disconnect();
    }
    
    // Create new observer to watch for DOM changes
    mutationObserverRef.current = new MutationObserver((mutations) => {
      let shouldRescan = false;
      
      mutations.forEach((mutation) => {
        // Check if nodes were added
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          // Check if any added nodes contain data-tour attributes
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              // Check if the element itself has data-tour or contains elements with data-tour
              if (element.hasAttribute("data-tour") || element.querySelector("[data-tour]")) {
                console.log("SimpleTour: Detected new tour element added to DOM");
                shouldRescan = true;
              }
            }
          });
        }
        
        // Check if attributes were modified (including data-tour)
        if (mutation.type === "attributes" && mutation.attributeName === "data-tour") {
          console.log("SimpleTour: Detected data-tour attribute change");
          shouldRescan = true;
        }
      });
      
      // Rescan for tour elements if changes detected
      if (shouldRescan) {
        console.log("SimpleTour: Rescanning for tour elements due to DOM changes");
        setTimeout(() => {
          const step = findCurrentStep();
          if (step && step.id !== currentStep?.id) {
            console.log("SimpleTour: Found new step after DOM change:", step.id);
            setCurrentStep(step);
            setHighlightedElement(step.selector);
          }
        }, 100); // Small delay to ensure DOM is fully updated
      }
    });
    
    // Start observing the document body for changes
    mutationObserverRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-tour"]
    });
    
    console.log("SimpleTour: MutationObserver setup complete");
  }, [isActive, dismissedTours, location.pathname, findCurrentStep, currentStep?.id]);
  
  // Calculate position based on step preference and element constraints
  const calculatePosition = useCallback((element: Element, messageBox: HTMLElement, position: string = "auto") => {
    const rect = element.getBoundingClientRect();
    const messageRect = messageBox.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    const spacing = 20; // Space between element and tooltip
    
    // Calculate positions for each direction
    const positions = {
      top: {
        top: rect.top + scrollTop - messageRect.height - spacing,
        left: rect.left + scrollLeft + (rect.width - messageRect.width) / 2
      },
      bottom: {
        top: rect.bottom + scrollTop + spacing,
        left: rect.left + scrollLeft + (rect.width - messageRect.width) / 2
      },
      left: {
        top: rect.top + scrollTop + (rect.height - messageRect.height) / 2,
        left: rect.left + scrollLeft - messageRect.width - spacing
      },
      right: {
        top: rect.top + scrollTop + (rect.height - messageRect.height) / 2,
        left: rect.right + scrollLeft + spacing
      }
    };
    
    // Check if position fits in viewport
    const fitsInViewport = (pos: { top: number, left: number }) => {
      return pos.top >= scrollTop + 20 &&
        pos.top + messageRect.height <= scrollTop + window.innerHeight - 20 &&
        pos.left >= 20 &&
        pos.left + messageRect.width <= window.innerWidth - 20;
    };
    
    // If specific position is requested and fits, use it
    if (position !== "auto" && positions[position as keyof typeof positions]) {
      const requestedPos = positions[position as keyof typeof positions];
      if (fitsInViewport(requestedPos)) {
        console.log("SimpleTour: Using requested position:", position);
        return requestedPos;
      } else {
        console.log("SimpleTour: Requested position", position, "does not fit, falling back to auto");
      }
    }
    
    // Auto positioning - try in order of preference: bottom, top, right, left
    const preferenceOrder: (keyof typeof positions)[] = ["bottom", "top", "right", "left"];
    
    for (const pos of preferenceOrder) {
      if (fitsInViewport(positions[pos])) {
        console.log("SimpleTour: Using auto position:", pos);
        return positions[pos];
      }
    }
    
    // Fallback to bottom with adjustments if nothing fits perfectly
    console.log("SimpleTour: Using fallback bottom position with adjustments");
    let fallbackPos = positions.bottom;
    
    // Adjust horizontal position if needed
    if (fallbackPos.left + messageRect.width > window.innerWidth - 20) {
      fallbackPos.left = window.innerWidth - messageRect.width - 20;
    }
    if (fallbackPos.left < 20) {
      fallbackPos.left = 20;
    }
    
    // Adjust vertical position if needed
    if (fallbackPos.top + messageRect.height > window.innerHeight + scrollTop - 20) {
      fallbackPos.top = scrollTop + (window.innerHeight - messageRect.height) / 2;
    }
    
    return fallbackPos;
  }, []);
  
  // Add this state and method near the top of the SimpleTour component:
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  const refreshTour = useCallback(() => {
    console.log("SimpleTour: Manual refresh triggered");
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Expose the refresh method globally
  useEffect(() => {
    (window as any).refreshTour = refreshTour;
    
    return () => {
      delete (window as any).refreshTour;
    };
  }, [refreshTour]);
  
  // Add this method wherever the dismissedTours Set is managed:
  const clearTourDismissed = useCallback(() => {
    console.log('SimpleTour Provider: Clearing dismissed state for page:', location.pathname);
    dismissedTours.delete(location.pathname);
  }, [location.pathname, dismissedTours]);

  // Add this useEffect to expose the method globally:
  useEffect(() => {
    (window as any).clearTourDismissed = clearTourDismissed;
    
    return () => {
      delete (window as any).clearTourDismissed;
    };
  }, [clearTourDismissed]);
  
  // Add this debug method after the refreshTour method:
  const debugTourScan = useCallback(() => {
    console.log('🔍 DEBUG: Manual tour scan triggered');
    console.log('🔍 DEBUG: isActive:', isActive, 'dismissed:', dismissedTours.has(location.pathname));
    
    const allTourElements = document.querySelectorAll('[data-tour]');
    console.log('🔍 DEBUG: All tour elements found:', allTourElements.length, Array.from(allTourElements).map(el => el.getAttribute('data-tour')));
    
    const step = findCurrentStep();
    console.log('🔍 DEBUG: findCurrentStep result:', step);
    
    if (step) {
      console.log('🔍 DEBUG: Setting step manually:', step.id);
      setCurrentStep(step);
      setHighlightedElement(step.selector);
      setIsVisible(true);
    }
  }, [isActive, dismissedTours, location.pathname, findCurrentStep]);

  // Add this useEffect to expose the debug method globally (add after the refreshTour exposure):
  useEffect(() => {
    (window as any).debugTourScan = debugTourScan;
    
    return () => {
      delete (window as any).debugTourScan;
    };
  }, [debugTourScan]);
  
  // Update the main useEffect to include refreshTrigger in dependencies:
  useEffect(() => {
    console.log("SimpleTour: Effect triggered - isActive:", isActive, "path:", location.pathname, "dismissed:", dismissedTours.has(location.pathname), "refreshTrigger:", refreshTrigger);
    
    if (!isActive || dismissedTours.has(location.pathname)) {
      console.log("SimpleTour: Tour not active or dismissed, clearing state");
      setCurrentStep(null);
      setHighlightedElement(null);
      setIsVisible(false);
      
      // Disconnect mutation observer
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
      return;
    }
    
    // Setup mutation observer to watch for dynamic content
    setupMutationObserver();
    
    // Add a small delay to ensure page elements are rendered
    const timer = setTimeout(() => {
      const step = findCurrentStep();
      console.log("SimpleTour: Found step:", step?.id, "current step:", currentStep?.id);
      
      if (step && step.id !== currentStep?.id) {
        console.log("SimpleTour: Setting new step:", step.id);
        setCurrentStep(step);
        setHighlightedElement(step.selector);
      } else if (!step) {
        console.log("SimpleTour: No step found, clearing state");
        setCurrentStep(null);
        setHighlightedElement(null);
        setIsVisible(false);
      }
    }, 500);
    
    return () => {
      clearTimeout(timer);
      // Cleanup mutation observer
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
    };
  }, [findCurrentStep, currentStep?.id, isActive, location.pathname, dismissedTours, setupMutationObserver]);
  
  // Position tour elements with smart or forced positioning
  const updatePosition = useCallback(() => {
    if (!highlightedElement || !targetElementRef.current || !currentStep) return;
    
    const element = targetElementRef.current;
    const rect = element.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    // Update overlay spotlight
    if (overlayRef.current) {
      const padding = 10;
      overlayRef.current.style.clipPath = `polygon(
                0% 0%, 
                0% 100%, 
                ${rect.left + scrollLeft - padding}px 100%, 
                ${rect.left + scrollLeft - padding}px ${rect.top + scrollTop - padding}px, 
                ${rect.right + scrollLeft + padding}px ${rect.top + scrollTop - padding}px, 
                ${rect.right + scrollLeft + padding}px ${rect.bottom + scrollTop + padding}px, 
                ${rect.left + scrollLeft - padding}px ${rect.bottom + scrollTop + padding}px, 
                ${rect.left + scrollLeft - padding}px 100%, 
                100% 100%, 
                100% 0%
            )`;
    }
    
    // Position message box using new positioning logic
    if (messageRef.current) {
      const messageBox = messageRef.current;
      const position = calculatePosition(element, messageBox, currentStep.position || "auto");
      
      messageBox.style.position = "absolute";
      messageBox.style.top = `${position.top}px`;
      messageBox.style.left = `${position.left}px`;
      messageBox.style.zIndex = "10001";
    }
  }, [highlightedElement, currentStep, calculatePosition]);
  
  // Smooth animation loop for position tracking
  const animatePosition = useCallback(() => {
    updatePosition();
    animationFrameRef.current = requestAnimationFrame(animatePosition);
  }, [updatePosition]);
  
  // Find and setup target element
  useEffect(() => {
    console.log("SimpleTour: Setup effect - isActive:", isActive, "highlightedElement:", highlightedElement, "dismissed:", dismissedTours.has(location.pathname));
    
    if (!isActive || !highlightedElement || dismissedTours.has(location.pathname)) {
      console.log("SimpleTour: Cleaning up - not active, no element, or dismissed");
      setIsVisible(false);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      targetElementRef.current = null;
      return;
    }
    
    // Function to find and setup element
    const setupElement = () => {
      const element = document.querySelector(highlightedElement);
      console.log("SimpleTour: Setup element - selector:", highlightedElement, "found:", !!element);
      
      if (!element) {
        // Retry after a short delay
        setTimeout(setupElement, 100);
        return;
      }
      
      console.log("SimpleTour: Element found, setting up...");
      targetElementRef.current = element;
      
      // Ensure element is visible
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center"
      });
      
      // Setup intersection observer to track visibility
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          console.log("SimpleTour: Intersection observer triggered, isIntersecting:", entry.isIntersecting);
          if (entry.isIntersecting) {
            setIsVisible(true);
            // Start animation loop
            if (!animationFrameRef.current) {
              animationFrameRef.current = requestAnimationFrame(animatePosition);
            }
          }
        },
        { threshold: 0.1 }
      );
      
      observerRef.current.observe(element);
    };
    
    setupElement();
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [highlightedElement, isActive, animatePosition, dismissedTours, location.pathname]);
  
  // Handle scroll and resize events
  useEffect(() => {
    const handleScroll = () => {
      if (isVisible && targetElementRef.current) {
        updatePosition();
      }
    };
    
    const handleResize = () => {
      if (isVisible && targetElementRef.current) {
        updatePosition();
      }
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [isVisible, updatePosition]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
    };
  }, []);
  
  // Handle close button click
  const handleClose = () => {
    if (onDismiss) {
      // Use dismiss function to just hide current tour without turning off guide
      onDismiss();
    } else if (onClose) {
      // Fallback to old behavior
      onClose();
    }
  };
  
  // Don't render if tour is not active, not visible, no current step, or page is dismissed
  console.log("SimpleTour: Render check - isActive:", isActive, "isVisible:", isVisible, "currentStep:", currentStep?.id, "dismissed:", dismissedTours.has(location.pathname));
  if (!isActive || !isVisible || !currentStep || dismissedTours.has(location.pathname)) return null;
  
  return (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-black bg-opacity-50 pointer-events-none transition-all duration-300"
        style={{ zIndex: 10000 }}
      />
      
      {/* Enhanced Message Box */}
      <div
        ref={messageRef}
        className="fixed pointer-events-auto animate-in fade-in-0 zoom-in-95 duration-300"
        style={{ zIndex: 10001 }}
      >
        {/* Main tooltip container with enhanced styling */}
        <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-sm overflow-hidden">
          {/* Subtle gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-white to-purple-50/30"></div>
          
          {/* Animated border glow */}
          <div
            className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 opacity-0 hover:opacity-100 transition-opacity duration-500 blur-sm"></div>
          
          <div className="relative p-5">
            {/* Header with icon and close button */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {/* Enhanced icon with gradient background */}
                <div className="relative">
                  <div
                    className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Lightbulb className="w-5 h-5 text-white" />
                  </div>
                  {/* Subtle glow effect */}
                  <div
                    className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl blur-md opacity-30 -z-10"></div>
                </div>
                
                {/* Title centered with icon */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 leading-tight">
                    {currentStep.title}
                  </h3>
                </div>
              </div>
              
              {/* Enhanced close button */}
              <button
                onClick={handleClose}
                className="group p-1.5 rounded-lg hover:bg-gray-100 transition-all duration-200 hover:scale-110"
                title="Dismiss this tip"
              >
                <X className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </button>
            </div>
            
            {/* Enhanced message */}
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              {currentStep.message}
            </p>
            
            {/* Enhanced footer */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              {/* Status indicator */}
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-gradient-to-r from-green-400 to-blue-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-gray-500">
                                    Guide Active
                                </span>
              </div>
              
              {/* Enhanced action button */}
              <button
                onClick={handleClose}
                className="group px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white text-xs font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
              >
                                <span className="flex items-center gap-1">
                                    Got it!
                                    <Sparkles className="w-3 h-3 group-hover:animate-spin" />
                                </span>
              </button>
            </div>
          </div>
          
          {/* Subtle bottom accent line */}
          <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
        </div>
      </div>
    </>
  );
};

export default SimpleTour;